import { FC } from 'react';
import { SupportedCurrencies, toBool } from 'vopay';
import { DropdownMenu, CircleButton } from 'UI';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useClientData } from '@/providers/ClientData.provider';

type ActionsMenuProps = {
  currency: SupportedCurrencies;
};

const ActionsMenu: FC<ActionsMenuProps> = ({ currency }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'Accounts.AccountBalances' });
  const history = useHistory();
  const { response } = useClientData();

  function isFundEnabled(currencyValue: string) {
    if (response.Country === 'CA') {
      return (
        (currencyValue === 'CAD' && toBool(response.EFTCollectEnabled)) ||
        (currencyValue === 'USD' && toBool(response.USDEFTCollectEnabled))
      );
    }

    if (response.Country === 'US') {
      return currencyValue === 'USD' && response.ACHFundEnabled === '1';
    }

    return false;
  }

  function isWithdrawEnabled(currencyValue: string) {
    if (response.Country === 'CA') {
      return (
        (currencyValue === 'CAD' && toBool(response.EFTSendEnabled)) ||
        (currencyValue === 'USD' && toBool(response.USDEFTSendEnabled))
      );
    }

    if (response.Country === 'US') {
      return currencyValue === 'USD' && response.ACHWithdrawEnabled === '1';
    }

    return false;
  }

  return (
    <DropdownMenu>
      <DropdownMenu.Trigger asChild>
        <CircleButton icon="more_horiz" colorScheme="action" circleFill />
      </DropdownMenu.Trigger>

      <DropdownMenu.Content sideOffset={8} align="end" onCloseAutoFocus={(e) => e.preventDefault()}>
        <DropdownMenu.Item
          onSelect={() => {
            history.push({
              pathname: '/fund-my-account',
              state: { currency },
            });
          }}
          disabled={!isFundEnabled(currency)}
        >
          {t('fund', { defaultValue: 'Fund' })}
        </DropdownMenu.Item>

        <DropdownMenu.Item
          onSelect={() => {
            history.push({
              pathname: '/withdraw-my-account',
              state: { currency },
            });
          }}
          disabled={!isWithdrawEnabled(currency)}
        >
          {t('withdraw', { defaultValue: 'Withdraw' })}
        </DropdownMenu.Item>

        <DropdownMenu.Item
          onSelect={() => {
            history.push({
              pathname: '/single-collection',
              state: { currency },
            });
          }}
          disabled={!isFundEnabled(currency)}
        >
          {t('collect', { defaultValue: 'Collect' })}
        </DropdownMenu.Item>

        <DropdownMenu.Item
          onSelect={() => {
            history.push({
              pathname: '/single-payment',
              state: { currency },
            });
          }}
          disabled={!isWithdrawEnabled(currency)}
        >
          {t('pay', { defaultValue: 'Pay' })}
        </DropdownMenu.Item>
      </DropdownMenu.Content>
    </DropdownMenu>
  );
};

export default ActionsMenu;
